import React from 'react';
import { Colors } from 'library';
import { Chip, IconButton, Row, Stack, Text } from 'library/ui';
import { format12HourTime } from 'shared/Dates';
import { Create } from '@mui/icons-material';

import type { TimesheetWorker, UnclockedInTimesheetWorker } from 'parent-portal/home/types';
import { CenterMissingPayment } from 'models';

export function needsReview(timesheet: TimesheetWorker | UnclockedInTimesheetWorker | CenterMissingPayment) {
    if (!('hour_report' in timesheet)) {
        return (
            <Chip variant="filled" bgColor={Colors.error} textColor={Colors.white}>
                Requires Approval
            </Chip>
        );
    }

    if (!timesheet.hour_report || !timesheet.hour_report.start) return null;

    if (timesheet.hour_report.business_approved)
        return (
            <Chip variant="filled" bgColor={Colors.secondary} textColor={Colors.white}>
                Approved
            </Chip>
        );

    if (timesheet.hour_report.end && !timesheet.hour_report.paid)
        return (
            <Chip variant="filled" bgColor={Colors.accent} textColor={Colors.white}>
                Needs Review
            </Chip>
        );
}

export function hoursReported(timesheet: TimesheetWorker | UnclockedInTimesheetWorker | CenterMissingPayment) {
    if (!('hour_report' in timesheet)) {
        return {
            middle: (
                <Text size="sm" weight="bold" component="div">
                    {format12HourTime(timesheet.start_date, timesheet.timezone)} -{' '}
                    {format12HourTime(timesheet.end_date, timesheet.timezone)}
                </Text>
            ),
            bottom: timesheet.break_minutes ? (
                <Text size="sm" component="div">
                    {timesheet.break_minutes} minute break
                </Text>
            ) : (
                <Text size="sm" component="div">
                    No break taken
                </Text>
            ),
        };
    }
    if (!timesheet.hour_report || !timesheet.hour_report.start)
        return {
            middle: (
                <Text size="sm" weight="bold" component="div">
                    Not clocked in
                </Text>
            ),
            bottom: null,
        };

    if (timesheet.hour_report.end) {
        return {
            middle: (
                <Text size="sm" weight="bold" component="div">
                    {format12HourTime(timesheet.hour_report.start, timesheet.timezone)} -{' '}
                    {format12HourTime(timesheet.hour_report.end, timesheet.timezone)}
                </Text>
            ),
            bottom: timesheet.hour_report.approved_break ? (
                <Text size="sm" component="div">
                    {timesheet.hour_report.approved_break} minute break
                </Text>
            ) : (
                <Text size="sm" component="div">
                    No break taken
                </Text>
            ),
        };
    }

    const clockedIn = {
        middle: (
            <Text size="sm" component="div">
                <b>Clock In:</b> {format12HourTime(timesheet.hour_report.start, timesheet.timezone)}
            </Text>
        ),
        bottom: null,
    };

    if (timesheet.hour_report.approved_break) {
        return {
            ...clockedIn,
            bottom: (
                <Text size="sm" component="div">
                    {timesheet.hour_report.approved_break} minute break
                </Text>
            ),
        };
    }

    return clockedIn;
}

export function actions(
    timesheet: TimesheetWorker | UnclockedInTimesheetWorker | CenterMissingPayment,
    editAction: (timesheet: TimesheetWorker | UnclockedInTimesheetWorker | CenterMissingPayment) => void,
    small?: boolean,
) {
    const align = small ? 'end' : 'start';

    if ('hour_report' in timesheet && timesheet?.hour_report?.paid) {
        return (
            <Chip variant="filled" bgColor={Colors.primary} textColor={Colors.white}>
                Paid
            </Chip>
        );
    }

    if ('hour_report' in timesheet && timesheet?.hour_report?.business_approved) {
        return (
            <Row gap="sm" align="center">
                <Stack gap="sm" align={align}>
                    {small && (
                        <Chip variant="filled" bgColor={Colors.secondary} textColor={Colors.white}>
                            Approved
                        </Chip>
                    )}
                    <Chip variant="filled" bgColor={Colors.primary} textColor={Colors.white}>
                        Pending
                    </Chip>
                </Stack>
                <IconButton icon={Create} onClick={() => editAction(timesheet)} />
            </Row>
        );
    }

    if (!('hour_report' in timesheet)) {
        return (
            <Stack gap="sm" align={align}>
                {small && (
                    <Chip variant="filled" bgColor={Colors.error} textColor={Colors.white}>
                        Requires Approval
                    </Chip>
                )}
                <IconButton icon={Create} onClick={() => editAction(timesheet)} />
            </Stack>
        );
    }

    return (
        <Stack gap="sm" align={align}>
            {small && timesheet?.hour_report?.end && (
                <Chip variant="filled" bgColor={Colors.accent} textColor={Colors.white}>
                    Needs Review
                </Chip>
            )}
            <IconButton icon={Create} onClick={() => editAction(timesheet)} />
        </Stack>
    );
}
